import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Message, Persona, Session } from "../types";
import PersonaAvatar from "./PersonaAvatar";
import { personaHueByIndex } from "./PersonaHue";
import { messageCountsByPersona } from "./personaMessageCounts";
import { useSessionStore } from "../store/sessionStore";
import SessionList from "./SessionList";

interface PersonaRailProps {
  personas: Persona[];
  activePersonaId: string;
  onSelectPersona: (id: string) => void;
  messages: Message[];
  sessions: Session[];
  activeSessionId: string | null;
  scenarioTitle: string | undefined;
  onNewSession: () => void;
  onRenameSession: (sessionId: string, newName: string | null) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
}

const PersonaRail: React.FC<PersonaRailProps> = ({
  personas,
  activePersonaId,
  onSelectPersona,
  messages,
  sessions,
  activeSessionId,
  scenarioTitle,
  onNewSession,
  onRenameSession,
  onDeleteSession,
}) => {
  // Starts collapsed on narrow viewports, mirroring the tool column: the 56px
  // stub is the narrow-width presentation of this rail, not a desktop-only
  // nicety. Read once at mount, like toolsCollapsed.
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024,
  );
  const [stubSessionsOpen, setStubSessionsOpen] = useState(false);

  // The stub's sessions popover is portalled to <body> and positioned from the
  // trigger's viewport rect. It cannot live in the rail's own subtree: the
  // aside clips with overflow-hidden so the collapse animation has something
  // to slide behind, and the stub clips again with overflow-y-auto so a long
  // cast can scroll. Overflow clipping is not a stacking question, so no
  // z-index on an absolutely positioned popover can escape either one.
  const sessionsTriggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ left: number; bottom: number } | null>(null);

  const editingSessionId = useSessionStore((s) => s.editingSessionId);
  const editingName = useSessionStore((s) => s.editingName);
  const setEditingSessionId = useSessionStore((s) => s.setEditingSessionId);

  const counts = messageCountsByPersona(
    messages,
    personas.map((p) => p.id),
  );

  // Collapsing while a rename is open would strand the edit in a column that
  // is no longer visible. Commit it the way the input's own blur does, rather
  // than carrying an editing state into the stub.
  const collapse = () => {
    if (editingSessionId) {
      const trimmed = editingName.trim();
      const id = editingSessionId;
      setEditingSessionId(null);
      void onRenameSession(id, trimmed === "" ? null : trimmed);
    }
    setCollapsed(true);
  };

  // Bottom-anchored so a tall list grows upward from the trigger rather than
  // off the bottom of the viewport.
  const positionPopover = useCallback(() => {
    const rect = sessionsTriggerRef.current?.getBoundingClientRect();
    if (rect) setPopoverPos({ left: rect.right + 4, bottom: window.innerHeight - rect.bottom });
  }, []);

  const popoverOpen = collapsed && stubSessionsOpen;
  useEffect(() => {
    if (!popoverOpen) return;
    positionPopover();
    // Capture phase: the stub is its own scroll container, so its scroll
    // events never reach window during the bubble phase.
    window.addEventListener("scroll", positionPopover, true);
    window.addEventListener("resize", positionPopover);
    return () => {
      window.removeEventListener("scroll", positionPopover, true);
      window.removeEventListener("resize", positionPopover);
    };
  }, [popoverOpen, positionPopover]);

  const rows = personas.map((persona, index) => ({
    persona,
    hue: personaHueByIndex(index),
    count: counts[persona.id] ?? 0,
  }));

  return (
    <aside
      className={`relative shrink-0 flex flex-col items-start overflow-hidden bg-surface-container-low border-r border-outline-variant/30 transition-[width] duration-200 ease-out motion-reduce:transition-none ${
        collapsed ? "w-14" : "w-60"
      }`}
    >
      {/* Full content keeps its width and is clipped by the aside's overflow,
          so nothing reflows while the rail animates — it slides aside and the
          transcript takes the space. */}
      <div
        className={`w-60 flex-1 min-h-0 flex flex-col transition-opacity duration-150 motion-reduce:transition-none ${
          collapsed ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        inert={collapsed}
      >
        <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5">
          <span className="font-label text-[10px] font-bold tracking-[0.2em] uppercase text-tertiary">
            Personas
          </span>
          <button
            type="button"
            onClick={collapse}
            title="Collapse personas"
            className="ml-auto p-1 rounded text-slate-500 hover:text-on-surface hover:bg-surface-container transition-colors"
          >
            <span className="material-symbols-outlined text-[17px]">left_panel_close</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-2 space-y-0.5">
          {rows.map(({ persona, hue, count }) => {
            const isActive = persona.id === activePersonaId;
            return (
              <button
                key={persona.id}
                type="button"
                onClick={() => onSelectPersona(persona.id)}
                className={`relative w-full flex items-center gap-2.5 pl-2 pr-2.5 py-2 rounded-md text-left transition-colors ${
                  isActive ? "bg-surface-container-high" : "hover:bg-surface-container"
                }`}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r"
                    style={{ background: hue }}
                  />
                )}
                <PersonaAvatar
                  name={persona.name}
                  src={persona.avatar}
                  className="w-9 h-9 rounded-md shrink-0"
                  textClassName="text-[12px] font-bold"
                  fallbackStyle={{
                    color: hue,
                    // Mirrors surface-container-high; the theme lives in
                    // Tailwind's JS config, so color-mix has no var() to use.
                    background: `color-mix(in srgb, ${hue} 15%, #222a3d)`,
                  }}
                />
                <span className="flex-1 min-w-0 flex flex-col">
                  <span
                    className={`font-label text-[13.5px] font-semibold truncate ${
                      isActive ? "text-on-surface" : "text-slate-400"
                    }`}
                  >
                    {persona.name}
                  </span>
                  {/* The role carries the persona's hue, so the colour that
                      marks their messages in the transcript is also what
                      identifies them here. Dimmed while inactive so the row
                      still reads as secondary to the selected one. */}
                  <span
                    className="font-label text-[11.5px] truncate"
                    style={{ color: hue, opacity: isActive ? 1 : 0.72 }}
                  >
                    {persona.role}
                  </span>
                </span>
                <span
                  className={`w-[18px] shrink-0 grid place-items-center font-label text-[10px] font-semibold tabular-nums text-slate-500 ${
                    count === 0 ? "opacity-40" : ""
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Mounted only while expanded; the stub's copy is gated on the
            opposite condition, so exactly one SessionList exists at a time. */}
        {!collapsed && (
          <div className="shrink-0 max-h-[42%] flex flex-col min-h-0">
            <SessionList
              sessions={sessions}
              activeSessionId={activeSessionId}
              scenarioTitle={scenarioTitle}
              onNewSession={onNewSession}
              onRenameSession={onRenameSession}
              onDeleteSession={onDeleteSession}
            />
          </div>
        )}
      </div>

      {/* The stub sits on top of the clipped content, so there is no layout
          change to animate — only opacity. */}
      <div
        className={`absolute inset-y-0 left-0 w-14 flex flex-col items-center gap-0.5 py-2.5 bg-surface-container-low border-r border-outline-variant/30 overflow-y-auto transition-opacity duration-150 delay-50 motion-reduce:transition-none ${
          collapsed ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        inert={!collapsed}
      >
        <button
          type="button"
          onClick={() => {
            // Not required for the single-mount invariant — the popover is
            // gated on `collapsed`, so expanding closes it in the same render.
            // This stops it reopening the next time the rail collapses.
            setStubSessionsOpen(false);
            setCollapsed(false);
          }}
          title="Expand personas"
          className="p-1.5 rounded text-slate-400 hover:text-on-surface hover:bg-surface-container transition-colors"
        >
          <span className="material-symbols-outlined text-[19px]">left_panel_open</span>
        </button>
        <span className="w-5 h-px bg-outline-variant/50 my-1 shrink-0" />

        {rows.map(({ persona, hue }) => {
          const isActive = persona.id === activePersonaId;
          return (
            <button
              key={persona.id}
              type="button"
              onClick={() => onSelectPersona(persona.id)}
              title={`${persona.name} — ${persona.role}`}
              className={`relative grid place-items-center w-10 py-1.5 rounded-md shrink-0 transition-colors ${
                isActive ? "bg-surface-container-high" : "hover:bg-surface-container"
              }`}
            >
              {isActive && (
                <span
                  className="absolute left-[-4px] top-1.5 bottom-1.5 w-0.5 rounded-r"
                  style={{ background: hue }}
                />
              )}
              <PersonaAvatar
                name={persona.name}
                src={persona.avatar}
                className="w-9 h-9 rounded-md"
                textClassName="text-[12px] font-bold"
                fallbackStyle={{
                  color: hue,
                  background: `color-mix(in srgb, ${hue} 15%, #222a3d)`,
                }}
              />
            </button>
          );
        })}

        <span className="w-5 h-px bg-outline-variant/50 my-1 shrink-0" />
        {/* 56px carries a cast but cannot carry a session list, so this is the
            one place the design accepts a popover for sessions. */}
        <div className="shrink-0">
          <button
            ref={sessionsTriggerRef}
            type="button"
            onClick={() => setStubSessionsOpen((o) => !o)}
            title="Sessions"
            aria-expanded={stubSessionsOpen}
            className="p-1.5 rounded text-slate-400 hover:text-on-surface hover:bg-surface-container transition-colors"
          >
            <span className="material-symbols-outlined text-[19px]">chat_bubble</span>
          </button>
          {/* Gated on `collapsed` as well as the toggle, so the expanded
              rail's inline SessionList and this copy can never be mounted at
              the same time. SessionList owns a document mousedown listener
              keyed on the shared menuOpenId; with two mounted, each closes the
              menu because the click landed outside ITS row, and a row menu
              could never be opened at all. */}
          {popoverOpen &&
            popoverPos &&
            createPortal(
              <>
                <div
                  className="fixed inset-0 z-[59]"
                  onClick={() => setStubSessionsOpen(false)}
                />
                <div
                  style={{ position: "fixed", left: popoverPos.left, bottom: popoverPos.bottom }}
                  className="z-[60] w-56 max-h-[70vh] flex flex-col min-h-0 rounded-lg bg-surface-container border border-outline-variant/60 shadow-2xl shadow-black/60"
                >
                  <SessionList
                    sessions={sessions}
                    activeSessionId={activeSessionId}
                    scenarioTitle={scenarioTitle}
                    onNewSession={onNewSession}
                    onRenameSession={onRenameSession}
                    onDeleteSession={onDeleteSession}
                  />
                </div>
              </>,
              document.body,
            )}
        </div>
      </div>
    </aside>
  );
};

export default PersonaRail;
